import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { MessageSquare, Pill, FileText } from "lucide-react-native";
import { CardTitleText, MoonDateField } from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import type { STDDetail } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { STD_OPTIONS } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";
import { useDateFormat } from "@src/packages/utils/useDateFormat";

export function STDDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();
  const { dateFormat } = useDateFormat();

  const getSTDLabel = (stdType: string): string => {
    const option = STD_OPTIONS.find((o) => o.id === stdType);
    return option ? t(option.labelKey) : stdType;
  };

  const updateSTD = (index: number, updates: Partial<STDDetail>) => {
    const updated = state.stds.map((s, i) =>
      i === index ? { ...s, ...updates } : s,
    );
    updateState({ stds: updated });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("STDDetails");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.std_details_title")}
      subtitle={t("med_history.gynae.sexual_health.std_details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        {state.stds.map((std, index) => (
          <View
            key={`${std.stdType}-${index}`}
            style={[GLASS_CARD_STYLE, styles.stdCard]}
          >
            <CardTitleText>
              {std.stdType === "other"
                ? t("med_history.gynae.sexual_health.std_other_card_title")
                : getSTDLabel(std.stdType)}
            </CardTitleText>
            {std.stdType === "other" && (
              <MedicalHistoryTextField
                label={t("med_history.gynae.sexual_health.other_label")}
                labelIcon={FileText}
                placeholder={t(
                  "med_history.gynae.sexual_health.std_other_placeholder",
                )}
                value={std.other ?? ""}
                onChangeText={(text) =>
                  updateSTD(index, { other: text || null })
                }
                style={{ width: "100%" }}
              />
            )}

            <MoonDateField
              label={t("med_history.gynae.sexual_health.std_diagnosed_label")}
              placeholder={dateFormat}
              value={std.dateOfDiagnosis ?? ""}
              onChange={(iso) =>
                updateSTD(index, { dateOfDiagnosis: iso || null })
              }
            />

            {std.stdType !== "other" && (
              <MedicalHistoryTextField
                label={t("med_history.gynae.sexual_health.treatment_label")}
                labelIcon={Pill}
                placeholder={t(
                  "med_history.gynae.sexual_health.std_treatment_placeholder",
                )}
                value={std.treatment ?? ""}
                onChangeText={(text) =>
                  updateSTD(index, { treatment: text || null })
                }
                style={{ width: "100%" }}
              />
            )}

            <MedicalHistoryTextField
              label={t("med_history.gynae.sexual_health.notes_label")}
              labelIcon={MessageSquare}
              placeholder={t(
                "med_history.gynae.sexual_health.std_notes_placeholder",
              )}
              value={std.notes ?? ""}
              onChangeText={(text) => updateSTD(index, { notes: text || null })}
              multiline
              numberOfLines={2}
              style={{ width: "100%" }}
            />
          </View>
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  stdCard: {
    gap: 16,
    padding: 16,
  },
});
